/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import FacilityService from "../services/FacilityService";
import QoCService from "../services/QoCService";
import { FacilityReportModal } from "./qoc/QoCFacilityReport";
import moment from "moment";
import { If, PositiveNumInput } from "../utils/Controls";
import { AntdPaging, AntdSelect, AntdTag } from "../utils/Antd";
import util from "../utils/util";
import plus from "../pages/assets/icons/plus.svg"
import edit from "../pages/assets/icons/pen-to-square-solid.svg"
import xmark from "../pages/assets/icons/circle-xmark-solid.svg"
import search from "../pages/assets/icons/magnifying-glass-solid.svg"
import * as ExcelJS from 'exceljs';
import {
    Input,
    Button,
    message,
    Modal,
    Checkbox,
    Tag,
    Card,
    Row,
    Col,
    Tooltip,
    Select,
    Divider,
    Progress,
    Spin,
    Table,
    InputNumber,
    Upload,
    Alert,
} from 'antd';
import {
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    FileTextOutlined,
    StarFilled,
    PrinterOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    SaveOutlined,
} from '@ant-design/icons';

const { confirm } = Modal;

// ─── Constants ────────────────────────────────────────────────────────────────
const REGIONS    = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];
const SITE_TYPES = [
    'Clinic without Maternity',
    'Clinic with Maternity',
    'Health Center',
    'Public Health Unit',
    'National Referral Hospital',
    'Regional Hospital',
];


// ─── Config Form ──────────────────────────────────────────────────────────────
const ConfigForm = forwardRef((props, ref) => {
    let { stationPoints, callback, pageno } = props;
    const [clientDtl, setClientDtl] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [data, setData] = useState([]);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [facilitiesOfSameLevel, setFacilitiesOfSameLevel] = useState([]);
    const [selectedFacilitiesToCopy, setSelectedFacilitiesToCopy] = useState([]);
    const [loadingFacilities, setLoadingFacilities] = useState(false);

    const save = () => {
        message.destroy();
        util.showLoader();
        FacilityService.saveConfig({ clientId: clientDtl.id, data: data.join() }).then(({ data }) => {
            message.success(data.message || 'Saved');
            setShowForm(false);
            callback(data.id ? pageno : 1);
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        });
    };

    const closeForm = () => setShowForm(false);

    const fetchFacilitiesOfSameLevel = async () => {
        if (!clientDtl.site_type) {
            message.warning('Current facility must have a level/site_type to copy station points');
            return;
        }
        setLoadingFacilities(true);
        try {
            const response = await FacilityService.all({ status: 1 });
            const facilities = response.data.result.data.filter(facility =>
                facility.id !== clientDtl.id &&
                facility.site_type === clientDtl.site_type
            );
            setFacilitiesOfSameLevel(facilities);
            setShowCopyModal(true);
        } catch (error) {
            message.error('Failed to fetch facilities of the same level');
        } finally {
            setLoadingFacilities(false);
        }
    };

    const copyStationPointsToFacilities = async () => {
        if (selectedFacilitiesToCopy.length === 0) {
            message.warning('Please select at least one facility to copy station points to');
            return;
        }
        if (data.length === 0) {
            message.warning('No station points selected to copy');
            return;
        }
        try {
            util.showLoader();
            const copyPromises = selectedFacilitiesToCopy.map(facilityId =>
                FacilityService.saveConfig({ clientId: facilityId, data: data.join() })
            );
            await Promise.all(copyPromises);
            message.success(`Station points copied to ${selectedFacilitiesToCopy.length} facilities successfully`);
            setShowCopyModal(false);
            setSelectedFacilitiesToCopy([]);
            callback(pageno);
        } catch (error) {
            message.error('Failed to copy station points to some facilities');
        } finally {
            util.hideLoader();
        }
    };

    useImperativeHandle(ref, () => ({
        openForm(id, name, site_type) {
            FacilityService.allStationPoints({ client_id: id }).then(res => {
                setData(res.data.result.data.map((v) => v.id));
            });
            setShowForm(true);
            setClientDtl({ id, name, site_type });
        }
    }));

    return (
        <>
            <Modal
                title={
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            Station Points - <span className="text-secondary">{clientDtl.name}</span>
                            {clientDtl.site_type && (
                                <span className="badge badge-info ml-2">{clientDtl.site_type}</span>
                            )}
                        </div>
                        <Button
                            type="default"
                            size="small"
                            onClick={fetchFacilitiesOfSameLevel}
                            loading={loadingFacilities}
                            disabled={!clientDtl.site_type || data.length === 0}
                        >
                            Copy to Same Level Facilities
                        </Button>
                    </div>
                }
                open={showForm}
                okText="Save"
                onOk={save}
                onCancel={closeForm}
                destroyOnClose
                maskClosable={false}
                width={1200}
                style={{ top: 20 }}
                bodyStyle={{ height: 'calc(100vh - 150px)' }}
            >
                <form onSubmit={e => { e.preventDefault(); save(); }} autoComplete="off" spellCheck="false">
                    {data.length > 0 && clientDtl.site_type && (
                        <div className="alert alert-info mb-3">
                            <small>
                                <strong>Tip:</strong> After saving, you can copy these {data.length} selected station points
                                to other facilities with the same level ({clientDtl.site_type}) using the "Copy to Same Level Facilities" button.
                            </small>
                        </div>
                    )}
                    <div className="form-group noselect">
                        <div className="row mingap">
                            {stationPoints.map((v, i) => (
                                <div key={i} className="col-md-3 mt5">
                                    <div className="form-control">
                                        <Checkbox
                                            checked={data.includes(v.id)}
                                            onChange={e => {
                                                let dt = [...data];
                                                if (e.target.checked) { dt.push(v.id); }
                                                else { dt.splice(dt.indexOf(v.id), 1); }
                                                setData(dt);
                                            }}
                                        >
                                            {v.name}
                                        </Checkbox>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            </Modal>

            <Modal
                title={`Copy Station Points to Other ${clientDtl.site_type} Facilities`}
                open={showCopyModal}
                onCancel={() => { setShowCopyModal(false); setSelectedFacilitiesToCopy([]); }}
                onOk={copyStationPointsToFacilities}
                okText={`Copy to ${selectedFacilitiesToCopy.length} Selected Facilities`}
                okButtonProps={{ disabled: selectedFacilitiesToCopy.length === 0 }}
                width={800}
                destroyOnClose
            >
                <div className="mb-3">
                    <div className="alert alert-warning">
                        <strong>Warning:</strong> This will replace all existing station points for the selected facilities
                        with the {data.length} station points currently selected for "{clientDtl.name}".
                    </div>
                </div>
                {facilitiesOfSameLevel.length === 0 ? (
                    <div className="text-center p-4">
                        <div className="text-muted">No other facilities found with level "{clientDtl.site_type}"</div>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Select facilities to copy station points to ({facilitiesOfSameLevel.length} available):</span>
                                <div>
                                    <Button size="small" type="link" onClick={() => setSelectedFacilitiesToCopy(facilitiesOfSameLevel.map(f => f.id))}>Select All</Button>
                                    <Button size="small" type="link" onClick={() => setSelectedFacilitiesToCopy([])}>Clear All</Button>
                                </div>
                            </div>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <div className="row">
                                {facilitiesOfSameLevel.map((facility) => (
                                    <div key={facility.id} className="col-md-6 mb-2">
                                        <div className="card" style={{
                                            border: selectedFacilitiesToCopy.includes(facility.id) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                            backgroundColor: selectedFacilitiesToCopy.includes(facility.id) ? '#f6ffed' : 'white'
                                        }}>
                                            <div className="card-body p-3">
                                                <Checkbox
                                                    checked={selectedFacilitiesToCopy.includes(facility.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setSelectedFacilitiesToCopy(prev => [...prev, facility.id]);
                                                        else setSelectedFacilitiesToCopy(prev => prev.filter(id => id !== facility.id));
                                                    }}
                                                >
                                                    <div>
                                                        <strong>{facility.business_name}</strong>
                                                        <div className="small text-muted">
                                                            RC: {facility.site_code} | Code: {facility.fac_code} | Region: {facility.located_region || 'N/A'}
                                                        </div>
                                                        <div className="small text-muted">
                                                            Current Station Points: {facility.station_points_count || 0}
                                                        </div>
                                                    </div>
                                                </Checkbox>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {selectedFacilitiesToCopy.length > 0 && (
                            <div className="mt-3 p-3 bg-light border rounded">
                                <strong>Selected for copying:</strong> {selectedFacilitiesToCopy.length} facilities will receive {data.length} station points
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
});


// ─── Cover Page Report Modal ──────────────────────────────────────────────────
const RATING_SCALE = [
    { min: 98,   max: 100,  label: 'Outstanding',          stars: 5, color: '#389e0d', bg: '#f6ffed' },
    { min: 95,   max: 97.9, label: 'Excellent',            stars: 4, color: '#1890ff', bg: '#e6f4ff' },
    { min: 90,   max: 94.9, label: 'Good',                 stars: 3, color: '#faad14', bg: '#fffbe6' },
    { min: 85,   max: 89.9, label: 'Fair',                 stars: 2, color: '#fa8c16', bg: '#fff7e6' },
    { min: 70,   max: 84.9, label: 'Requires Improvement', stars: 1, color: '#f5222d', bg: '#fff2f0' },
    { min: 0,    max: 69.9, label: 'Inadequate',           stars: 0, color: '#820014', bg: '#ffebe8' },
];
const getRating = s => RATING_SCALE.find(r => s >= r.min && s <= r.max) || RATING_SCALE[RATING_SCALE.length - 1];

const CLAUSES = [
    { code: 'AACC', title: 'Access, Assessment and Continuity of Care' },
    { code: 'COP',  title: 'Care of the Patient' },
    { code: 'MOM',  title: 'Management of Medication' },
    { code: 'PRPE', title: 'Patient Rights and Patient Education' },
    { code: 'IPCM', title: 'Infection Prevention and Control Management' },
    { code: 'PSQI', title: 'Patient Safety and Quality Improvement' },
    { code: 'GROM', title: 'Governance, Roles and Responsibilities of Management' },
    { code: 'FMS',  title: 'Facility Management, Safety and Support Services' },
    { code: 'HRE',  title: 'Human Resource and Education' },
    { code: 'ISM',  title: 'Information System Management' },
];

const CL_COLOR = {
    AACC: '#0ea5e9', COP: '#8b5cf6', MOM: '#f59e0b',  PRPE: '#10b981',
    IPCM: '#ef4444', PSQI: '#3b82f6', GROM: '#6366f1', FMS: '#ec4899',
    HRE:  '#14b8a6', ISM: '#a855f7',
};

function Stars({ count, size = 14 }) {
    return (
        <span>
            {[1, 2, 3, 4, 5].map(i => (
                <StarFilled key={i} style={{ color: i <= count ? '#faad14' : '#e8e8e8', fontSize: size }} />
            ))}
        </span>
    );
}

const CoverPageModal = React.forwardRef((props, ref) => {
    const [open, setOpen]               = useState(false);
    const [facility, setFacility]       = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [selectedId, setSelectedId]   = useState(null);
    const [report, setReport]           = useState(null);
    const [loading, setLoading]         = useState(false);

    React.useImperativeHandle(ref, () => ({
        open(facilityRow) {
            setFacility(facilityRow);
            setReport(null);
            setSelectedId(null);
            setLoading(true);
            QoCService.getAssessments({ facility_id: facilityRow.id, status: 'approved' })
                .then(res => {
                    const list = res.data?.data || [];
                    setAssessments(list);
                    if (list.length) { setSelectedId(list[0].id); loadReport(list[0].id); }
                    else setLoading(false);
                })
                .catch(() => setLoading(false));
            setOpen(true);
        }
    }));

    const loadReport = (assessmentId) => {
        setLoading(true);
        setReport(null);
        Promise.all([
            QoCService.getAssessmentDetail(assessmentId),
            QoCService.getClauseScores(assessmentId),
        ]).then(([detailRes, scoresRes]) => {
            setReport({ detail: detailRes.data?.data || {}, scores: scoresRes.data?.data || [] });
        }).catch(() => {}).finally(() => setLoading(false));
    };

    const handleSelectAssessment = (id) => { setSelectedId(id); loadReport(id); };

    if (!facility) return null;

    const assessment = assessments.find(a => a.id === selectedId);
    const overallPct = report ? parseFloat(report.detail.overall_percentage || 0) : 0;
    const rating     = getRating(overallPct);
    const clauseMap  = {};
    (report?.scores || []).forEach(s => { clauseMap[s.clause_code] = s; });

    return (
        <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={860}
            style={{ top: 20 }} bodyStyle={{ padding: 0 }} destroyOnClose>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: 12, backgroundColor: '#fafafa' }}>
                <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>QoC Audit Cover Page</span>
                <span style={{ color: '#888', fontSize: 13 }}>—</span>
                <span style={{ color: '#1890ff', fontWeight: 600 }}>{facility.business_name}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Select style={{ minWidth: 220 }} value={selectedId} onChange={handleSelectAssessment}
                        placeholder="Select assessment period" notFoundContent="No approved assessments">
                        {assessments.map(a => (
                            <Select.Option key={a.id} value={a.id}>
                                {moment(a.assessment_date).format('DD MMM YYYY')}
                                {a.overall_percentage ? ` — ${parseFloat(a.overall_percentage).toFixed(1)}%` : ''}
                            </Select.Option>
                        ))}
                    </Select>
                    <Tooltip title="Print cover page">
                        <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
                    </Tooltip>
                </div>
            </div>

            <div id="qoc-cover-print" style={{ padding: '24px 28px', minHeight: 400 }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
                ) : !assessments.length ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
                        <FileTextOutlined style={{ fontSize: 48, display: 'block', marginBottom: 12 }} />
                        No approved assessments found for this facility
                    </div>
                ) : !report ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
                ) : (
                    <>
                        <div style={{ textAlign: 'center', borderBottom: '3px solid #1890ff', paddingBottom: 16, marginBottom: 20 }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#1890ff', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Eswatini Ministry of Health
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginTop: 4 }}>
                                Eswatini Quality of Care (QoC) Standards Audit
                            </div>
                        </div>

                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                    {[
                                        ['Facility Name',     facility.business_name],
                                        ['Type of Facility',  facility.site_type || 'N/A'],
                                        ['Facility Address',  facility.address || 'N/A'],
                                        ['Scope of Services', facility.scope_of_services || 'N/A'],
                                    ].map(([k, v]) => (
                                        <tr key={k}>
                                            <td style={{ padding: '4px 8px 4px 0', color: '#888', width: 130, verticalAlign: 'top' }}>{k}:</td>
                                            <td style={{ padding: '4px 0', fontWeight: 600, color: '#333' }}>{v}</td>
                                        </tr>
                                    ))}
                                </table>
                            </Col>
                            <Col span={12}>
                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                    {[
                                        ['Audit Date',       assessment ? moment(assessment.assessment_date).format('DD MMMM YYYY') : '—'],
                                        ['License Number',   facility.fac_code || 'N/A'],
                                        ['RC',               facility.site_code || 'N/A'],
                                        ['Region',           facility.located_region || 'N/A'],
                                        ['Facility Owner',   facility.facility_owner || 'N/A'],
                                    ].map(([k, v]) => (
                                        <tr key={k}>
                                            <td style={{ padding: '4px 8px 4px 0', color: '#888', width: 130, verticalAlign: 'top' }}>{k}:</td>
                                            <td style={{ padding: '4px 0', fontWeight: 600, color: '#333' }}>{v}</td>
                                        </tr>
                                    ))}
                                </table>
                            </Col>
                        </Row>

                        <div style={{ backgroundColor: rating.bg, border: `2px solid ${rating.color}`, borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>OVERALL SCORE</div>
                                <div style={{ fontSize: 36, fontWeight: 900, color: rating.color, lineHeight: 1 }}>{overallPct.toFixed(1)}%</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <Stars count={rating.stars} size={22} />
                                <div style={{ fontSize: 13, fontWeight: 700, color: rating.color, marginTop: 4 }}>{rating.label}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <Progress type="circle" percent={Math.min(100, overallPct)} width={70} strokeColor={rating.color} format={() => `${overallPct.toFixed(0)}%`} />
                            </div>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#333' }}>SECTION SCORES</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f0f5ff' }}>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '2px solid #d6e4ff', width: 50 }}>#</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '2px solid #d6e4ff' }}>Clause / Section</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '2px solid #d6e4ff', width: 80 }}>Score</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '2px solid #d6e4ff', width: 90 }}>Stars</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '2px solid #d6e4ff', width: 180 }}>Interpretation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {CLAUSES.map((cl, idx) => {
                                        const s   = clauseMap[cl.code];
                                        const pct = s ? parseFloat(s.total_score || 0) : null;
                                        const r   = pct !== null ? getRating(pct) : null;
                                        const cc  = CL_COLOR[cl.code] || '#1890ff';
                                        return (
                                            <tr key={cl.code} style={{ backgroundColor: idx % 2 ? '#fafafa' : '#fff' }}>
                                                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                                    <span style={{ display: 'inline-block', width: 22, height: 22, borderRadius: '50%', backgroundColor: cc, color: '#fff', textAlign: 'center', lineHeight: '22px', fontSize: 11, fontWeight: 700 }}>{idx + 1}</span>
                                                </td>
                                                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                                    <span style={{ fontWeight: 700, color: cc }}>{cl.code}</span>
                                                    <span style={{ color: '#555', marginLeft: 6 }}>{cl.title}</span>
                                                </td>
                                                <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                                    {pct !== null ? <span style={{ fontWeight: 800, fontSize: 14, color: r.color }}>{pct.toFixed(1)}%</span> : <span style={{ color: '#ccc' }}>—</span>}
                                                </td>
                                                <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                                    {r ? <Stars count={r.stars} size={11} /> : <span style={{ color: '#ccc' }}>—</span>}
                                                </td>
                                                <td style={{ padding: '7px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                                    {r ? <span style={{ color: r.color, fontWeight: 600, fontSize: 11 }}>{r.label}</span> : <span style={{ color: '#ccc' }}>Not assessed</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#666' }}>SCORING SCALE</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {RATING_SCALE.map(r => (
                                    <div key={r.label} style={{ padding: '4px 10px', borderRadius: 6, backgroundColor: r.bg, border: `1px solid ${r.color}`, fontSize: 11 }}>
                                        <Stars count={r.stars} size={10} />
                                        <span style={{ color: r.color, fontWeight: 700, marginLeft: 4 }}>{r.label}</span>
                                        <span style={{ color: '#888', marginLeft: 4 }}>{r.min}–{r.max}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />
                        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#666' }}>AUDIT TEAM</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Role</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Name</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Areas Covered</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e8e8e8', width: 100 }}>Date</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', width: 130 }}>Signature</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>Lead Auditor</td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>{report.detail.assessor_name || '—'}</td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', color: '#ccc' }}>All clauses</td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>{assessment ? moment(assessment.assessment_date).format('DD/MM/YY') : '—'}</td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 130 }}></td>
                                </tr>
                                {[1, 2, 3].map(n => (
                                    <tr key={n}>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', color: '#aaa' }}>Auditor {n}</td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 140 }}></td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 140 }}></td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px dotted #d9d9d9' }}></td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 130 }}></td>
                                    </tr>
                                ))}
                                <tr>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>Reviewed & Approved by</td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 140 }}></td>
                                    <td></td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px dotted #d9d9d9' }}></td>
                                    <td style={{ padding: '8px 10px', borderBottom: '1px dotted #d9d9d9', minWidth: 130 }}></td>
                                </tr>
                            </tbody>
                        </table>

                        <div style={{ marginTop: 16, padding: '10px 14px', backgroundColor: '#f9f9f9', borderRadius: 6, fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                            Comments / Free text:
                            <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, minHeight: 50, marginTop: 6, backgroundColor: '#fff', padding: 8 }}>
                                {report.detail.comments || ''}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <style>{`@media print { body > * { display: none !important; } #qoc-cover-print { display: block !important; } .ant-modal-mask, .ant-modal-wrap { display: none !important; } }`}</style>
        </Modal>
    );
});


// ─── Scheduled Clauses ────────────────────────────────────────────────────────
const STATUS_CFG = {
    scheduled:   { color: '#1890ff', icon: '📅', label: 'Scheduled'   },
    in_progress: { color: '#faad14', icon: '🔄', label: 'In Progress' },
    completed:   { color: '#52c41a', icon: '✓',  label: 'Completed'   },
    overdue:     { color: '#f5222d', icon: '⚠',  label: 'Overdue'     },
    cancelled:   { color: '#999',    icon: '✗',  label: 'Cancelled'   },
};
const CL_COLORS = {
    AACC: '#0ea5e9', COP: '#8b5cf6', MOM: '#f59e0b',  PRPE: '#10b981',
    IPCM: '#ef4444', PSQI: '#3b82f6', GROM: '#6366f1', FMS: '#ec4899',
    HRE:  '#14b8a6', ISM: '#a855f7',
};

function ScheduledClauses({ facilityId }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading]     = useState(true);
    useEffect(() => {
        const today = moment();
        QoCService.getAuditSchedules({
            facility_id: facilityId,
            from_date: today.clone().startOf('year').format('YYYY-MM-DD'),
            to_date:   today.clone().endOf('year').format('YYYY-MM-DD'),
        }).then(res => {
            setSchedules((res.data?.data || []).filter(s => s.status !== 'cancelled'));
        }).catch(() => {}).finally(() => setLoading(false));
    }, [facilityId]);

    if (loading) return <span style={{ color: '#ccc', fontSize: 11 }}>loading…</span>;
    if (!schedules.length) return <span style={{ color: '#ccc', fontSize: 11 }}>No active schedules</span>;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {schedules.map(s => {
                const cfg       = STATUS_CFG[s.status] || STATUS_CFG.scheduled;
                const cc        = s.clause_code ? (CL_COLORS[s.clause_code] || '#1890ff') : '#6366f1';
                const isOverdue = s.status === 'overdue';
                const canStart  = s.status === 'scheduled' || s.status === 'in_progress';
                return (
                    <Tooltip key={s.id} title={
                        <div style={{ fontSize: 12 }}>
                            <div><strong>{s.clause_code ? `${s.clause_code} — ${s.clause_title}` : 'Full Audit'}</strong></div>
                            <div>Date: {moment(s.scheduled_date).format('DD MMM YYYY')}</div>
                            {s.lead_assessor_name && <div>Lead: {s.lead_assessor_name}</div>}
                            <div>Status: {cfg.label}</div>
                            {isOverdue && <div style={{ color: '#ffa39e' }}>Past scheduled date</div>}
                            {canStart  && <div style={{ color: '#91d5ff' }}>Click to start assessment</div>}
                        </div>
                    }>
                        <div
                            onClick={() => { if (canStart) window.location.href = `/qoc/assessments?schedule=${s.id}&facility=${facilityId}`; }}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                cursor: canStart ? 'pointer' : 'default',
                                backgroundColor: isOverdue ? '#fff2f0' : `${cc}15`,
                                border: `1px solid ${isOverdue ? '#ffccc7' : cc}`,
                                color: isOverdue ? '#f5222d' : cc,
                                transition: 'all 0.18s',
                            }}
                        >
                            <span>{cfg.icon}</span>
                            <span>{s.clause_code || 'Full'}</span>
                            {canStart && <span style={{ opacity: 0.6 }}>▶</span>}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
}


// ─── Search Form ──────────────────────────────────────────────────────────────
const SearchForm = (props) => {
    let { dataRef, onSearch } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const handleChange = (v, k) => { data[k] = v; setData({ ...data }); };
    useEffect(() => { dataRef.current = { ...data }; }, [data]);
    useEffect(() => { setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps }); }, [dataRef.current.p, dataRef.current.ps]);
    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                <div><Input placeholder="Search" allowClear value={data.k} onChange={e => handleChange(e.target.value, 'k')} /></div>
                <div><Button type="primary" icon={<img src={search} alt='' height={15} />} onClick={() => onSearch()}></Button></div>
            </div>
        </form>
    );
};


// ─── Add / Edit Form ──────────────────────────────────────────────────────────
const AddForm = forwardRef((props, ref) => {
    let { callback, pageno } = props;
    const [showForm, setShowForm] = useState(false);
    let [data, setData] = useState({});
    const handleChange = (v, k) => { data[k] = v; setData({ ...data }); };
    const save = () => {
        message.destroy();
        util.showLoader();
        FacilityService.save(data).then(({ data }) => {
            message.success(data.message || 'Saved');
            callback(data.id ? pageno : 1);
            setShowForm(false);
        }).catch(e => { message.error(e.message); }).finally(() => { util.hideLoader(); });
    };
    useImperativeHandle(ref, () => ({
        openForm(dtl) {
            setData(dtl ? { ...dtl } : { is_national: '0', status: '1' });
            setShowForm(true);
        }
    }));
    return (
        <Modal title={`${data.id ? 'Edit' : 'Add'} Facility`} open={showForm}
            okText="Save" onOk={save} onCancel={() => setShowForm(false)}
            destroyOnClose maskClosable={false} width={1000}>
            <form onSubmit={e => { e.preventDefault(); save(); }} autoComplete="off" spellCheck="false">
                {util.getUserType() === 'ADMIN' &&
                    <div className="row mingap">
                        <div className="col-md-3 form-group">
                            <label>Is National?</label>
                            <div><AntdSelect options={[{ value: '0', label: "No" }, { value: '1', label: "Yes" }]} value={data.is_national} onChange={v => handleChange(v, 'is_national')} /></div>
                        </div>
                    </div>
                }
                <div className="mb15">
                    <Card size="small" type="inner" title="Facility Detail">
                        <div className="row mingap">
                            <div className="col-md-3 form-group"><label className="req">RC</label><Input value={data.site_code || ''} onChange={e => handleChange(e.target.value, 'site_code')} /></div>
                            <div className="col-md-3 form-group"><label className="req">Facility Code</label><Input value={data.fac_code || ''} onChange={e => handleChange(e.target.value, 'fac_code')} /></div>
                            <div className="col-md-6 form-group"><label className="req">Facility Name</label><Input value={data.business_name || ''} onChange={e => handleChange(e.target.value, 'business_name')} /></div>
                            <div className="col-md-3 form-group"><label>Facility Owner</label><Input value={data.facility_owner || ''} onChange={e => handleChange(e.target.value, 'facility_owner')} /></div>
                            <div className="col-md-3 form-group">
                                <label>Facility Type</label>
                                <div><AntdSelect options={SITE_TYPES} value={data.site_type} onChange={v => handleChange(v, 'site_type')} /></div>
                            </div>
                            <div className="col-md-3 form-group"><label className="req">Facility Tel</label><Input value={data.phone1 || ''} onChange={e => handleChange(e.target.value, 'phone1')} /></div>
                            <div className="col-md-3 form-group">
                                <label>Region</label>
                                <div><AntdSelect options={REGIONS} value={data.located_region} onChange={v => handleChange(v, 'located_region')} /></div>
                            </div>
                            <div className="col-md-12 form-group"><label className="req">Facility Address</label><Input value={data.address || ''} onChange={e => handleChange(e.target.value, 'address')} /></div>
                            <div className="col-md-3 form-group"><label>City</label><Input value={data.city || ''} onChange={e => handleChange(e.target.value, 'city')} /></div>
                            <div className="col-md-3 form-group"><label>Pincode</label><Input value={data.pincode || ''} onChange={e => handleChange(e.target.value, 'pincode')} /></div>
                            <div className="col-md-3 form-group"><label>Latitude</label><Input type="number" value={data.latitude || ''} onChange={e => handleChange(e.target.value, 'latitude')} /></div>
                            <div className="col-md-3 form-group"><label>Longitude</label><Input type="number" value={data.longitude || ''} onChange={e => handleChange(e.target.value, 'longitude')} /></div>
                        </div>
                    </Card>
                </div>
                <div className="mb15">
                    <Card size="small" type="inner" title="Patient Volume Baseline (for Survey Sampling)"
                        extra={<span style={{ fontSize: 11, color: '#888' }}>Used to calculate minimum survey requirements</span>}>
                        <div className="row mingap">
                            <div className="col-md-4 form-group">
                                <label>Avg Monthly Inpatients</label>
                                <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 450"
                                    value={data.avg_monthly_inpatients || ''} onChange={v => handleChange(v, 'avg_monthly_inpatients')} />
                                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Average inpatient census per month</div>
                            </div>
                            <div className="col-md-4 form-group">
                                <label>Avg Monthly Outpatients</label>
                                <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 1200"
                                    value={data.avg_monthly_outpatients || ''} onChange={v => handleChange(v, 'avg_monthly_outpatients')} />
                                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Average outpatient visits per month</div>
                            </div>
                            <div className="col-md-4 form-group">
                                <label>Survey Sample Rate (%)</label>
                                <InputNumber style={{ width: '100%' }} min={1} max={100} placeholder="10"
                                    value={data.survey_sample_pct || 10} onChange={v => handleChange(v, 'survey_sample_pct')}
                                    formatter={v => `${v}%`} parser={v => v.replace('%', '')} />
                                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                                    {data.avg_monthly_inpatients || data.avg_monthly_outpatients ? (
                                        <>Min IP surveys: <strong>{Math.ceil((data.avg_monthly_inpatients || 0) * (data.survey_sample_pct || 10) / 100)}</strong>
                                            &nbsp;| Min OP surveys: <strong>{Math.ceil((data.avg_monthly_outpatients || 0) * (data.survey_sample_pct || 10) / 100)}</strong></>
                                    ) : 'Enter volumes above to auto-calculate minimum surveys'}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
                <div className="mb15">
                    <Card size="small" type="inner" title="Admin User Detail">
                        <div className="row mingap">
                            <div className="col-md-4 form-group"><label className="req">Name</label><Input value={data.name || ''} onChange={e => handleChange(e.target.value, 'name')} /></div>
                            <div className="col-md-4 form-group"><label className="req">Email</label><Input value={data.email || ''} onChange={e => handleChange(e.target.value, 'email')} /></div>
                            <div className="col-md-4 form-group"><label className="req">Mobile</label><PositiveNumInput className="form-control" value={data.mobile || ''} onChange={v => handleChange(v, 'mobile')} type="int" maxLength="10" /></div>
                            <div className="col-md-4 form-group"><label className="req">Username</label><Input value={data.username || ''} onChange={e => handleChange(e.target.value, 'username')} /></div>
                            <div className="col-md-4 form-group"><label>Password</label><Input value={data.password || ''} onChange={e => handleChange(e.target.value, 'password')} /></div>
                        </div>
                    </Card>
                </div>
                <div className="row mingap">
                    <div className="col-md-3 form-group">
                        <label className="req">Status</label>
                        <div><AntdSelect options={[{ value: '1', label: "Active" }, { value: '0', label: "Inactive" }]} value={data.status} onChange={v => handleChange(v, 'status')} /></div>
                    </div>
                </div>
            </form>
        </Modal>
    );
});


// ─── Bulk Upload Helpers ──────────────────────────────────────────────────────
async function downloadFacilityTemplate() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Facilities');
    ws.columns = [
        { header: 'facility_name',  key: 'facility_name',  width: 40 },
        { header: 'region',         key: 'region',         width: 20 },
        { header: 'site_type',      key: 'site_type',      width: 28 },
        { header: 'site_code',      key: 'site_code',      width: 15 },
        { header: 'fac_code',       key: 'fac_code',       width: 15 },
        { header: 'phone',          key: 'phone',          width: 18 },
        { header: 'address',        key: 'address',        width: 30 },
        { header: 'facility_owner', key: 'facility_owner', width: 25 },
        { header: 'admin_email',    key: 'admin_email',    width: 30 },
        { header: 'admin_mobile',   key: 'admin_mobile',   width: 18 },
    ];
    ws.getRow(1).eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1890FF' } };
    });
    ws.addRow({ facility_name: 'Mbabane Government Hospital', region: 'Hhohho',  site_type: 'Regional Hospital',        site_code: 'RC001', fac_code: 'F001', phone: '2404-2111', address: 'Mbabane',        facility_owner: 'Government', admin_email: 'admin@mbabane.gov.sz',   admin_mobile: '76001001' });
    ws.addRow({ facility_name: "Pigg's Peak Health Centre",   region: 'Hhohho',  site_type: 'Clinic without Maternity', site_code: 'RC002', fac_code: 'F002', phone: '2437-1100', address: "Pigg's Peak",    facility_owner: 'Government', admin_email: 'admin@piggspeak.gov.sz', admin_mobile: '76001002' });
    ws.addRow({ facility_name: 'Raleigh Fitkin Memorial',     region: 'Manzini', site_type: 'National Referral Hospital',site_code: 'RC003', fac_code: 'F003', phone: '2505-2211', address: 'Manzini',        facility_owner: 'Mission',    admin_email: 'admin@rfm.org.sz',      admin_mobile: '76001003' });

    const vws = wb.addWorksheet('Valid Values');
    vws.addRow(['Regions', 'Site Types']);
    vws.getRow(1).eachCell(c => { c.font = { bold: true }; });
    const maxLen = Math.max(REGIONS.length, SITE_TYPES.length);
    for (let i = 0; i < maxLen; i++) vws.addRow([REGIONS[i] || '', SITE_TYPES[i] || '']);

    const buf  = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'facility_bulk_upload_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
}

async function parseFacilityFile(file) {
    const ext  = file.name.split('.').pop().toLowerCase();
    const rows = [];
    if (ext === 'csv') {
        const text    = await file.text();
        const lines   = text.split(/\r?\n/).filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ''));
        for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
            if (!cols[0]) continue;
            const row = {};
            headers.forEach((h, idx) => { row[h] = cols[idx] || ''; });
            rows.push(row);
        }
    } else {
        const buf = await file.arrayBuffer();
        const wb  = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        const ws = wb.worksheets[0];

        // Auto-detect header row: find the first row containing 'facility_name'
        let headerRowNum = 1;
        ws.eachRow((row, rowNum) => {
            if (headerRowNum !== 1) return;
            let found = false;
            row.eachCell(cell => {
                if (String(cell.value || '').trim().toLowerCase() === 'facility_name') found = true;
            });
            if (found) headerRowNum = rowNum;
        });

        const headers = [];
        ws.getRow(headerRowNum).eachCell((cell, colNum) => {
            headers[colNum] = String(cell.value || '').trim().toLowerCase().replace(/[^a-z_]/g, '');
        });
        ws.eachRow((row, rowNum) => {
            if (rowNum <= headerRowNum) return;
            const obj = {};
            row.eachCell((cell, colNum) => { if (headers[colNum]) obj[headers[colNum]] = String(cell.value ?? '').trim(); });
            if (Object.values(obj).some(v => v)) rows.push(obj);
        });
    }
    return rows;
}

function normaliseFacilityRow(raw, idx) {
    const name     = raw.facility_name || raw.name || raw.facility || '';
    const region   = raw.region        || raw.located_region || '';
    const siteType = raw.site_type     || raw.type || raw.facility_type || raw.level || '';
    const siteCode = raw.site_code     || raw.rc || '';
    const facCode  = raw.fac_code      || raw.facility_code || '';
    const owner    = raw.facility_owner|| raw.owner || '';
    const phone    = raw.phone         || raw.phone1 || raw.telephone || '';
    const address  = raw.address       || raw.facility_address || '';

    const errors = [];
    if (!name) errors.push('Missing facility name');
    const matchedRegion = REGIONS.find(r => r.toLowerCase() === region.toLowerCase()) || '';
    if (!matchedRegion && region) errors.push(`Unknown region: "${region}"`);
    const matchedType = SITE_TYPES.find(t => t.toLowerCase() === siteType.toLowerCase()) || '';
    if (!matchedType && siteType) errors.push(`Unknown type: "${siteType}"`);

    const adminEmail    = raw.admin_email    || raw.email    || '';
    const adminMobile   = raw.admin_mobile   || raw.mobile   || '';
    // Use username from file if provided, otherwise generate from name on save
    const adminUsername = raw.username       || raw.admin_username || '';
    const adminPassword = raw.password       || raw.admin_password || '123456';

    return {
        key:            idx,
        business_name:  name,
        located_region: matchedRegion || region,
        site_type:      matchedType   || siteType,
        site_code:      siteCode,
        fac_code:       facCode,
        facility_owner: owner,
        phone1:         phone,
        address:        address,
        admin_email:    adminEmail,
        admin_mobile:   adminMobile,
        admin_username: adminUsername,
        admin_password: adminPassword,
        _errors:   errors,
        _status:   'pending',
        _msg:      '',
        _username: '',
    };
}


// ─── Bulk Upload Modal ────────────────────────────────────────────────────────
const BulkUploadModal = forwardRef(({ onDone }, ref) => {
    const [open,     setOpen]     = useState(false);
    const [rows,     setRows]     = useState([]);
    const [step,     setStep]     = useState(0); // 0=upload 1=review 2=saving
    const [progress, setProgress] = useState(0);
    const [saving,   setSaving]   = useState(false);
    const abortRef = useRef(false);

    useImperativeHandle(ref, () => ({
        open() { setOpen(true); setRows([]); setStep(0); setProgress(0); }
    }));

    const handleFile = async file => {
        try {
            const raw = await parseFacilityFile(file);
            if (!raw.length) { message.error('No data rows found in file'); return false; }
            setRows(raw.map((r, i) => normaliseFacilityRow(r, i)));
            setStep(1);
        } catch (e) { message.error('Parse error: ' + e.message); }
        return false; // prevent antd auto-upload
    };

    const updateRow = (idx, field, val) => {
        setRows(prev => prev.map(r => {
            if (r.key !== idx) return r;
            const u = { ...r, [field]: val };
            const errors = [];
            if (!u.business_name) errors.push('Missing facility name');
            if (u.located_region && !REGIONS.includes(u.located_region))   errors.push(`Unknown region: "${u.located_region}"`);
            if (u.site_type      && !SITE_TYPES.includes(u.site_type))     errors.push(`Unknown type: "${u.site_type}"`);
            return { ...u, _errors: errors };
        }));
    };

    const handleSave = async () => {
        const toSave = rows.filter(r => r._status !== 'saved' && !r._errors.length);
        if (!toSave.length) { message.warning('No valid rows to save'); return; }
        setSaving(true); setStep(2); setProgress(0); abortRef.current = false;
        let done = 0;
        for (const row of toSave) {
            if (abortRef.current) break;
            try {
                // Slug used for auto-generating several fields
                const slug     = row.business_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                // Admin user credentials — from file columns if present, else auto-generated
                const username = (row.admin_username && row.admin_username.trim()) ? row.admin_username.trim() : slug;
                const password = (row.admin_password && row.admin_password.trim()) ? row.admin_password.trim() : '123456';
                const email    = (row.admin_email    && row.admin_email.trim())    ? row.admin_email.trim()    : `${slug}@facility.local`;
                const mobile   = (row.admin_mobile   && row.admin_mobile.trim())   ? row.admin_mobile.trim()   : '00000000';
                // Required facility fields — use file value or placeholder (update via Edit after import)
                const site_code = (row.site_code && row.site_code.trim()) ? row.site_code.trim() : `RC-${slug.substring(0, 8).toUpperCase()}`;
                const fac_code  = (row.fac_code  && row.fac_code.trim())  ? row.fac_code.trim()  : `F-${slug.substring(0, 8).toUpperCase()}`;
                const phone1    = (row.phone1    && row.phone1.trim())    ? row.phone1.trim()    : '00000000';
                const address   = (row.address   && row.address.trim())   ? row.address.trim()   : row.located_region || 'Eswatini';

                await FacilityService.save({
                    business_name:  row.business_name,
                    located_region: row.located_region,
                    site_type:      row.site_type,
                    facility_owner: row.facility_owner,
                    status:         '1',
                    site_code,
                    fac_code,
                    phone1,
                    address,
                    asset_cat_ids:  row.asset_cat_ids || '1', // satisfies CI required/empty check; no real asset cats in QoC
                    name:           row.business_name,
                    username,
                    password,
                    email,
                    mobile,
                });
                setRows(prev => prev.map(r => r.key === row.key ? { ...r, _status: 'saved', _username: username } : r));
            } catch (e) {
                const msg = e?.response?.data?.message || e.message || 'Error';
                setRows(prev => prev.map(r => r.key === row.key ? { ...r, _status: 'error', _msg: msg } : r));
            }
            done++;
            setProgress(Math.round((done / toSave.length) * 100));
        }
        setSaving(false);
        if (onDone) onDone();
    };

    const validCount  = rows.filter(r => !r._errors.length).length;
    const errorCount  = rows.filter(r =>  r._errors.length).length;
    const savedCount  = rows.filter(r => r._status === 'saved').length;
    const failedCount = rows.filter(r => r._status === 'error').length;

    const inlineInput = (val, idx, field) => (
        <input
            style={{ width: '100%', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 6px', fontSize: 13 }}
            value={val || ''}
            onChange={e => updateRow(idx, field, e.target.value)}
        />
    );

    const reviewColumns = [
        { title: '#', width: 45, render: (_, __, i) => <span style={{ color: '#999' }}>{i + 1}</span> },
        {
            title: 'Facility Name', dataIndex: 'business_name',
            render: (val, row) => row._status === 'saved' ? val : inlineInput(val, row.key, 'business_name'),
        },
        {
            title: 'Region', dataIndex: 'located_region', width: 150,
            render: (val, row) => row._status === 'saved' ? (val || '—') : (
                <Select size="small" style={{ width: '100%' }} value={val || undefined} placeholder="Select" allowClear
                    onChange={v => updateRow(row.key, 'located_region', v)}>
                    {REGIONS.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
                </Select>
            ),
        },
        {
            title: 'Level', dataIndex: 'site_type', width: 120,
            render: (val, row) => row._status === 'saved' ? (val || '—') : (
                <Select size="small" style={{ width: '100%' }} value={val || undefined} placeholder="Select" allowClear
                    onChange={v => updateRow(row.key, 'site_type', v)}>
                    {SITE_TYPES.map(t => <Select.Option key={t} value={t}>{t}</Select.Option>)}
                </Select>
            ),
        },
        {
            title: 'RC', dataIndex: 'site_code', width: 90,
            render: (val, row) => row._status === 'saved' ? (val || '—') : inlineInput(val, row.key, 'site_code'),
        },
        {
            title: 'Fac Code', dataIndex: 'fac_code', width: 90,
            render: (val, row) => row._status === 'saved' ? (val || '—') : inlineInput(val, row.key, 'fac_code'),
        },
        {
            title: 'Status', width: 130,
            render: (_, row) => {
                if (row._status === 'saved')  return <Tag color="success" icon={<CheckCircleOutlined />}>Saved</Tag>;
                if (row._status === 'error')  return <Tooltip title={row._msg}><Tag color="error"   icon={<WarningOutlined />}>Failed</Tag></Tooltip>;
                if (row._errors.length)       return <Tooltip title={row._errors.join('; ')}><Tag color="warning" icon={<WarningOutlined />}>Fix needed</Tag></Tooltip>;
                return <Tag color="blue">Ready</Tag>;
            },
        },
        {
            title: '', width: 40,
            render: (_, row) => row._status !== 'saved'
                ? <Button size="small" type="text" danger onClick={() => setRows(prev => prev.filter(r => r.key !== row.key))}>✕</Button>
                : null,
        },
    ];

    const resultColumns = [
        { title: 'Facility Name', dataIndex: 'business_name' },
        { title: 'Region',        dataIndex: 'located_region', width: 130 },
        { title: 'Level',         dataIndex: 'site_type',      width: 100 },
        { title: 'RC',            dataIndex: 'site_code',      width: 80  },
        {
            title: 'Username (auto-generated)',
            width: 220,
            render: (_, row) => row._status === 'saved'
                ? <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#1890ff' }}>{row._username}</span>
                : <span style={{ color: '#ccc' }}>—</span>,
        },
        {
            title: 'Result', width: 130,
            render: (_, row) => {
                if (row._status === 'saved') return <Tag color="success" icon={<CheckCircleOutlined />}>Saved</Tag>;
                if (row._status === 'error') return <Tooltip title={row._msg}><Tag color="error">Failed</Tag></Tooltip>;
                return <Tag>Skipped</Tag>;
            },
        },
    ];

    return (
        <Modal
            title={<span><FileExcelOutlined style={{ color: '#52c41a', marginRight: 8 }} />Bulk Upload Facilities</span>}
            open={open}
            onCancel={() => { if (!saving) setOpen(false); }}
            footer={null}
            width={1100}
            style={{ top: 20 }}
            destroyOnClose
            maskClosable={false}
        >
            {/* ── Step 0: Upload ── */}
            {step === 0 && (
                <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ flex: 1 }}>
                        <p style={{ color: '#555', marginBottom: 8 }}>
                            Upload an Excel (.xlsx) or CSV file. Required: <code>facility_name</code>.
                            Optional but recommended: <code>site_code</code>, <code>fac_code</code>, <code>phone</code>, <code>address</code>, <code>region</code>, <code>site_type</code>, <code>facility_owner</code>, <code>admin_email</code>, <code>admin_mobile</code>.
                            Missing values are auto-generated as placeholders — update via Edit afterwards.
                        </p>
                        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#614700' }}>
                            <strong>🔑 Admin credentials:</strong> Each facility gets a user account.
                            If <code>username</code> column is present it will be used; otherwise auto-generated from the facility name (slugified).
                            If <code>password</code> column is present it will be used; otherwise defaults to <code style={{ background: '#fff7cc', padding: '1px 5px', borderRadius: 3 }}>123456</code>.
                            If <code>admin_email</code> is blank, a placeholder <code style={{ background: '#fff7cc', padding: '1px 5px', borderRadius: 3 }}>slug@facility.local</code> is used.
                            If <code>admin_mobile</code> is blank, <code style={{ background: '#fff7cc', padding: '1px 5px', borderRadius: 3 }}>00000000</code> is used.
                        </div>
                        <Upload.Dragger accept=".xlsx,.xls,.csv" beforeUpload={handleFile} showUploadList={false}>
                            <p className="ant-upload-drag-icon">
                                <FileExcelOutlined style={{ fontSize: 36, color: '#52c41a' }} />
                            </p>
                            <p className="ant-upload-text">Click or drag Excel / CSV file here</p>
                            <p className="ant-upload-hint">.xlsx, .xls, .csv supported</p>
                        </Upload.Dragger>
                    </div>
                    <div style={{ width: 230 }}>
                        <Button icon={<DownloadOutlined />} onClick={downloadFacilityTemplate} block style={{ marginBottom: 12 }}>
                            Download Template
                        </Button>
                        <div style={{ fontSize: 12, color: '#888', lineHeight: 1.7 }}>
                            <strong>Valid Regions:</strong><br />
                            {REGIONS.join(', ')}<br /><br />
                            <strong>Valid Types:</strong><br />
                            {SITE_TYPES.join(', ')}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Step 1: Review ── */}
            {step === 1 && (
                <>
                    {errorCount > 0 && (
                        <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                            message={`${errorCount} row(s) need attention — fix inline or remove. They will be skipped on save.`} />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ color: '#888', fontSize: 13 }}>
                            {rows.length} rows &nbsp;·&nbsp;
                            <strong style={{ color: '#1890ff' }}>{validCount} ready</strong>
                            {errorCount > 0 && <> &nbsp;·&nbsp; <strong style={{ color: '#fa8c16' }}>{errorCount} with issues</strong></>}
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button onClick={() => { setRows([]); setStep(0); }}>Re-upload</Button>
                            <Button type="primary" icon={<SaveOutlined />} disabled={validCount === 0} onClick={handleSave}>
                                Save {validCount} Facilit{validCount === 1 ? 'y' : 'ies'}
                            </Button>
                        </div>
                    </div>
                    <Table
                        dataSource={rows} columns={reviewColumns} size="small"
                        pagination={{ pageSize: 20, showSizeChanger: false }}
                        rowClassName={row =>
                            row._status === 'saved'  ? 'bulk-row-saved'  :
                            row._status === 'error'  ? 'bulk-row-error'  :
                            row._errors.length       ? 'bulk-row-warn'   : ''
                        }
                    />
                    <style>{`
                        .bulk-row-saved td { background: #f6ffed !important; }
                        .bulk-row-error td { background: #fff1f0 !important; }
                        .bulk-row-warn  td { background: #fffbe6 !important; }
                    `}</style>
                </>
            )}

            {/* ── Step 2: Saving / Results ── */}
            {step === 2 && (
                <div style={{ padding: '8px 0' }}>
                    {saving ? (
                        <>
                            <p style={{ color: '#555' }}>Saving facilities… please wait.</p>
                            <Progress percent={progress} status="active" style={{ marginBottom: 12 }} />
                            <Button danger onClick={() => { abortRef.current = true; }}>Stop</Button>
                        </>
                    ) : (
                        <>
                            <Alert
                                type={failedCount > 0 ? 'warning' : 'success'} showIcon style={{ marginBottom: 16 }}
                                message={failedCount > 0
                                    ? `${savedCount} saved successfully, ${failedCount} failed`
                                    : `All ${savedCount} facilit${savedCount === 1 ? 'y' : 'ies'} saved successfully`}
                            />
                            <Table dataSource={rows} columns={resultColumns} size="small" pagination={{ pageSize: 20 }} />
                            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                                {failedCount > 0 && (
                                    <Button onClick={() => {
                                        setRows(prev => prev.map(r => r._status === 'error' ? { ...r, _status: 'pending', _msg: '' } : r));
                                        setStep(1);
                                    }}>
                                        Retry Failed
                                    </Button>
                                )}
                                <Button type="primary" onClick={() => setOpen(false)}>Done</Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
});


// ─── Main Facilities Component ────────────────────────────────────────────────
export default function Facilities() {
    // Permission checks
    const canAdd    = util.canAdd('manage_facilities');
    const canEdit   = util.canEdit('manage_facilities');
    const canDelete = util.canDelete('manage_facilities');
    const canExport = util.canExport('manage_facilities');
    const [result, setResult]               = useState({ data: [], page: {} });
    const sdataRef  = useRef({ p: 1, ps: 25 });
    const formRef   = useRef();
    const configRef = useRef();
    const coverRef  = useRef();
    const bulkRef   = useRef();
    const [reportFacility, setReportFacility] = useState(null);
    const [stationPoints, setStationPoints]   = useState([]);

    const list = (p, ps) => {
        sdataRef.current.p  = p  || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        util.showLoader();
        FacilityService.list(sdataRef.current).then(({ data }) => {
            setResult(data.result);
        }).catch(e => { message.error(e.message); }).finally(() => { util.hideLoader(); });
    };

    const deleteRecord = (id) => {
        message.destroy();
        confirm({
            title: 'Do you Want to delete this facility?',
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes', okType: 'danger', cancelText: 'No',
            onOk() {
                util.showLoader();
                FacilityService.delete(id).then(({ data }) => {
                    message.success(data.message || 'Deleted');
                    list();
                }).catch(e => { message.error(e.message); }).finally(() => { util.hideLoader(); });
            },
        });
    };

    useEffect(() => {
        list();
        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints(res.data.result.data.sort((a, b) => ('' + a.name).localeCompare(b.name)));
        });
        return () => { message.destroy(); };
    }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Facilities</h2></div>
            </div>
            <div className="page-pad">
                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        Showing {result.page.start + 1} – {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }
                <div className="d-flex tbl-search-head">
                    <div className="my-auto"><SearchForm dataRef={sdataRef} onSearch={list} /></div>
                    <div className="ml-auto my-auto" style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={() => bulkRef.current.open()}>
                            <FileExcelOutlined /> Bulk Upload
                        </Button>
                        {canAdd && <Button type="primary" onClick={() => formRef.current.openForm()}>
                            <img src={plus} alt='' className='mr5' height={15} /> Add
                        </Button>}
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-md table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid">
                                <tr>
                                    <th className="w20">SN</th>
                                    <th>Facility / Site</th>
                                    <th>Scheduled Clauses</th>
                                    <th className="w60">Status</th>
                                    <th className="w80">Report</th>
                                    <th className="w70"></th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top">
                                {result.data.map((v, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}.</td>
                                        <td>
                                            <strong>{v.business_name}</strong>
                                            <div className="note-text pt3">
                                                <div className="d-flex"><div className="w80">RC</div><div className="bold600 pl5">: {v.site_code}</div></div>
                                                <div className="d-flex"><div className="w80">Fac Code</div><div className="bold600 pl5">: {v.fac_code}</div></div>
                                                <div className="d-flex"><div className="w80">Owner</div><div className="bold600 pl5">: {v.facility_owner || 'N/A'}</div></div>
                                                <div className="d-flex"><div className="w80">Level</div><div className="bold600 pl5">: {v.site_type || 'N/A'}</div></div>
                                                <div className="d-flex"><div className="w80">Region</div><div className="bold600 pl5">: {v.located_region || 'N/A'}</div></div>
                                            </div>
                                        </td>
                                        <td><ScheduledClauses facilityId={v.id} /></td>
                                        <td className="nowrap">
                                            {v.status ? <AntdTag type="success">Active</AntdTag> : <AntdTag type="danger">Inactive</AntdTag>}
                                        </td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                            <Button size="small" type="primary" ghost icon={<FileTextOutlined />}
                                                onClick={() => coverRef.current.open(v)} style={{ fontSize: 11, marginRight: 4 }}>
                                                Cover
                                            </Button>
                                            <Button size="small" danger ghost icon={<FileTextOutlined />}
                                                onClick={() => setReportFacility(v)} style={{ fontSize: 11 }}>
                                                Full PDF
                                            </Button>
                                        </td>
                                        <td className="text-center">
                                            <Button.Group size="small">
                                                {canEdit && <Button type="default" onClick={() => formRef.current.openForm(v)}>
                                                    <img src={edit} alt='' height={15} />
                                                </Button>}
                                                {canDelete && <Button type="default" onClick={() => deleteRecord(v.id)}
                                                    disabled={util.getUserType() !== 'ADMIN' && v.is_national * 1}>
                                                    <img src={xmark} alt='' height={15} />
                                                </Button>}
                                            </Button.Group>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging onChange={list} total={result.page.total_records}
                            current={result.page.cur_page} pageSize={sdataRef.current.ps} showSizeChanger />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>

            <CoverPageModal ref={coverRef} />
            {reportFacility && (
                <FacilityReportModal facilityProp={reportFacility} onClose={() => setReportFacility(null)} />
            )}
            <AddForm    ref={formRef}   callback={(p) => list(p)} pageno={1} assetCats={[]} />
            <ConfigForm ref={configRef} callback={(p) => list(p)} pageno={1} stationPoints={stationPoints} />
            <BulkUploadModal ref={bulkRef} onDone={() => list(1)} />
        </div>
    );
}




